import React, { JSX, useMemo } from 'react';
import { CellProps, Column } from 'react-table';
import { Flex, Text } from '@chakra-ui/react';
import { Modal, ModalType, Table } from 'common/components';

import { TABLE_CELL_EMPTY_VALUE } from '../../../../common/components/Table/utils/table.const';
import { getLocalDateTimeByUtcTimestamp } from '../../../../common/utils';
import { useTranslations } from '../../../../contexts/LocalizationContext';
import { AssignedItemResponse } from '../model/assigned-item.response';
import { getItemTableCell, getReturnedAtRestaurant, mapAssignedItemToEcoFeeReason } from '../utils/eco-fee.helper';

export type BorrowingDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  items: AssignedItemResponse[];
  header?: string;
};

export const BorrowingDetailsModal = ({ isOpen, onClose, items, header }: BorrowingDetailsModalProps): JSX.Element => {
  const t = useTranslations();
  const modalColumns: Column<AssignedItemResponse>[] = useMemo(
    () => [
      { id: 'orderId', Header: t('users_eco_fee.borrowing_details.table.order_id'), accessor: 'orderId' as const },
      {
        id: 'productDetails',
        Header: t('users_eco_fee.common.item'),
        Cell: ({
          cell: {
            row: { original },
          },
        }) => <Text>{getItemTableCell(original.product, t)}</Text>,
      },
      {
        id: 'reason',
        Header: t('users_eco_fee.common.reason'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<AssignedItemResponse>) => (
          <Flex justifyContent={'center'} alignItems={'center'}>
            <Text>{mapAssignedItemToEcoFeeReason(original, t)}</Text>
          </Flex>
        ),
      },
      {
        id: 'borrowedAt',
        Header: t('users_eco_fee.borrowing_details.table.borrowed_at'),
        Cell: ({
          cell: {
            row: { original },
          },
        }) => (
          <>
            <Text>{getLocalDateTimeByUtcTimestamp(original.period.start.timestamp)}</Text>
            <Text>{original.restaurant?.restaurantName}</Text>
          </>
        ),
      },
      {
        id: 'returnedAt',
        Header: t('users_eco_fee.borrowing_details.table.returned_at'),
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          const endTs = original.period.end?.timestamp;
          const returnedAt = endTs ? getLocalDateTimeByUtcTimestamp(endTs) : null;
          const returnRestaurant = getReturnedAtRestaurant(original);
          if (returnRestaurant && returnedAt) {
            return (
              <>
                <Text>{returnedAt}</Text>
                <Text>{returnRestaurant.restaurantName}</Text>
              </>
            );
          }
          return <>{TABLE_CELL_EMPTY_VALUE}</>;
        },
      },
      {
        id: 'returnDeadline',
        Header: t('users_eco_fee.borrowing_details.table.return_deadline'),
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          const dueTs = original.period.due?.timestamp;
          const deadline = dueTs ? getLocalDateTimeByUtcTimestamp(dueTs) : TABLE_CELL_EMPTY_VALUE;
          return <>{deadline}</>;
        },
      },
      {
        id: 'twoFactorReturn',
        Header: t('users_eco_fee.borrowing_details.table.two_factor_return'),
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          const isPendingReturn = original.pendingReturnPeriod !== null;
          return isPendingReturn ? (
            <>
              <Text>
                {t('users_eco_fee.borrowing_details.restaurant_scan', {
                  '{{date}}': original.pendingReturnPeriod?.end?.timestamp
                    ? getLocalDateTimeByUtcTimestamp(original.pendingReturnPeriod.end.timestamp)
                    : TABLE_CELL_EMPTY_VALUE,
                })}
              </Text>
              <Text>
                {t('users_eco_fee.borrowing_details.restaurant_scan_deadline', {
                  '{{date}}': original.pendingReturnPeriod?.due?.timestamp
                    ? getLocalDateTimeByUtcTimestamp(original.pendingReturnPeriod.due.timestamp)
                    : TABLE_CELL_EMPTY_VALUE,
                })}
              </Text>
            </>
          ) : (
            <>{TABLE_CELL_EMPTY_VALUE}</>
          );
        },
      },
    ],
    [t],
  );

  return (
    <Modal
      isModalVisible={isOpen}
      onModalClose={onClose}
      onConfirmClick={onClose}
      modalHeader={header ?? t('users_eco_fee.borrowing_details.title')}
      size={'5xl'}
      type={ModalType.INFO}
    >
      <Table<AssignedItemResponse> data={items} columns={modalColumns} isLoading={false} />
    </Modal>
  );
};
